import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader, DataTable, Pill } from "../components/Table";
import { UserCircle, Plus, Trash, PencilSimple, X } from "@phosphor-icons/react";

const BLANK = { name: "", company: "", mobile: "", email: "", password: "", address: "", city: "", state: "", enabled: true };

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [meta, setMeta] = useState({ can_manage: false });
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(null); // {mode:'new'|'edit', data}
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/customers").then((r) => { setCustomers(r.data.customers); setMeta({ can_manage: r.data.can_manage }); });
  useEffect(() => { load(); }, []);

  const save = async () => {
    const d = modal.data;
    if (!d.name.trim()) return toast.error("Customer name required");
    setBusy(true);
    try {
      if (modal.mode === "new") { await api.post("/customers", d); toast.success("Customer created"); }
      else { await api.put(`/customers/${d.id}`, d); toast.success("Customer updated"); }
      setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete customer "${c.name}"? Their login is removed and vehicles are unassigned (not deleted).`)) return;
    try { await api.delete(`/customers/${c.id}`); toast.success("Customer deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const filtered = customers.filter((c) => !q ||
    [c.name, c.company, c.mobile, c.email].some((v) => String(v || "").toLowerCase().includes(q.toLowerCase())));

  const set = (k, v) => setModal((m) => ({ ...m, data: { ...m.data, [k]: v } }));

  const columns = [
    { key: "name", header: "Customer", render: (r) => <span className="font-semibold">{r.name}</span> },
    { key: "company", header: "Company", render: (r) => r.company || "—" },
    { key: "mobile", header: "Mobile", render: (r) => <span className="font-mono text-xs">{r.mobile || "—"}</span> },
    { key: "email", header: "Login Email", render: (r) => <span className="font-mono text-xs">{r.email || <span className="text-muted-foreground">no login</span>}</span> },
    { key: "vehicle_count", header: "Vehicles", render: (r) => <Pill color="#0091e6">{r.vehicle_count}</Pill> },
    { key: "enabled", header: "Status", render: (r) => r.enabled ? <Pill color="#34C759">Active</Pill> : <Pill color="#FF9500">Disabled</Pill> },
  ];
  if (meta.can_manage) {
    columns.push({ key: "act", header: "", render: (r) => (
      <span className="flex gap-2">
        <button data-testid={`edit-cust-${r.id}`} onClick={() => setModal({ mode: "edit", data: { ...BLANK, ...r, password: "" } })}
          className="text-muted-foreground hover:text-primary"><PencilSimple size={15} weight="bold" /></button>
        <button data-testid={`del-cust-${r.id}`} onClick={() => del(r)} className="text-muted-foreground hover:text-destructive"><Trash size={15} weight="bold" /></button>
      </span>
    )});
  }

  return (
    <div className="p-5 space-y-4">
      <PageHeader title="Customers" subtitle={`${customers.length} client accounts`}
        actions={meta.can_manage && <button data-testid="add-customer" onClick={() => setModal({ mode: "new", data: { ...BLANK } })}
          className="flex items-center gap-1.5 bg-primary text-white text-sm font-semibold px-3 py-2 rounded-sm hover:opacity-90"><Plus size={15} weight="bold" /> Add Customer</button>} />

      <input data-testid="cust-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / company / mobile / email"
        className="bg-card border border-border rounded-sm px-3 py-1.5 text-sm w-80 focus:ring-1 focus:ring-primary focus:outline-none" />

      <DataTable testid="customers-table" columns={columns} rows={filtered} empty="No customers yet — add your first client" />

      {modal && (
        <div className="fixed inset-0 z-[3000] bg-black/60 flex items-center justify-center p-4" data-testid="customer-modal">
          <div className="bg-card border border-border rounded-sm p-5 w-full max-w-lg space-y-3 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><UserCircle size={20} weight="bold" className="text-primary" />
                <span className="font-display font-bold text-lg">{modal.mode === "new" ? "New Customer" : "Edit Customer"}</span></div>
              <button onClick={() => setModal(null)} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="cust-name" value={modal.data.name} onChange={(e) => set("name", e.target.value)} placeholder="Customer name *" className="col-span-2 bg-background border border-border rounded-sm px-2 py-2 text-sm" />
              <input value={modal.data.company} onChange={(e) => set("company", e.target.value)} placeholder="Company" className="bg-background border border-border rounded-sm px-2 py-2 text-sm" />
              <input data-testid="cust-mobile" value={modal.data.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="Mobile" className="bg-background border border-border rounded-sm px-2 py-2 text-sm" />
              <input data-testid="cust-email" value={modal.data.email} onChange={(e) => set("email", e.target.value)} placeholder="Login email (optional)" className="bg-background border border-border rounded-sm px-2 py-2 text-sm font-mono" disabled={modal.mode === "edit"} />
              <input value={modal.data.password} onChange={(e) => set("password", e.target.value)} placeholder={modal.mode === "edit" ? "(login set at creation)" : "Login password"} type="password" className="bg-background border border-border rounded-sm px-2 py-2 text-sm" disabled={modal.mode === "edit"} />
              <input value={modal.data.address} onChange={(e) => set("address", e.target.value)} placeholder="Address" className="col-span-2 bg-background border border-border rounded-sm px-2 py-2 text-sm" />
              <input value={modal.data.city} onChange={(e) => set("city", e.target.value)} placeholder="City" className="bg-background border border-border rounded-sm px-2 py-2 text-sm" />
              <input value={modal.data.state} onChange={(e) => set("state", e.target.value)} placeholder="State" className="bg-background border border-border rounded-sm px-2 py-2 text-sm" />
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={modal.data.enabled} onChange={(e) => set("enabled", e.target.checked)} /> Account enabled</label>
            {modal.mode === "new" && modal.data.email && <p className="text-xs text-muted-foreground">A login account will be created so this customer can track their own vehicles.</p>}
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setModal(null)} className="px-4 py-2 text-sm font-semibold border border-border rounded-sm">Cancel</button>
              <button data-testid="cust-save" disabled={busy} onClick={save} className="px-4 py-2 text-sm font-semibold bg-primary text-white rounded-sm disabled:opacity-50">{busy ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
